import { AfterViewInit, Component, computed, HostListener, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { ChallengeProgressiveReward, LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { NativeService } from '@app/shared/services/native.service';
import { firstValueFrom, Observable } from 'rxjs';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { WalletComponent } from '@app/shared/components/wallet/wallet.component';
import { AdventureQuestChallengeBoxComponent } from './adventure-quest-challenge-box/adventure-quest-challenge-box.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { AdventureQuestRewardBoxComponent } from './adventure-quest-reward-box/adventure-quest-reward-box.component';
import { ProgressBarComponent } from '@app/shared/components/progress-bar/progress-bar.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';

type RewardAssetSizeType = 'reward-one' | 'reward-two' | 'reward-three';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-adventure-quest-modal',
  templateUrl: './adventure-quest.component.html',
  styleUrls: ['./adventure-quest.component.scss'],
  standalone: true,
  imports: [
    AdventureQuestChallengeBoxComponent,
    AdventureQuestRewardBoxComponent,
    ProgressBarComponent,
    SharedModule,
    AnimatedTimerComponent,
    WalletComponent,
  ],
})
export class AdventureQuestModalComponent implements OnInit, AfterViewInit {
  @HostListener('window:resize') onResizeWindow() {
    this.checkArrows();
  }

  componentDestroy: () => Observable<unknown>;

  eventId: string;
  showArrows = signal(false);
  rewardAssetSize: RewardAssetSizeType[] = [];
  orderedProgressiveChallenge: ChallengeProgressiveReward[] = [];
  redeemingPositions = signal(new Set<number>());

  private _adventureQuestCopy: LimitedTimeEvent;

  private readonly eventManager = inject(EventManager);
  public readonly logger = inject(LoggerService);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly nativeService = inject(NativeService);

  isLoadingByIndex = computed(() => {
    const positions = this.redeemingPositions();
    return (this.adventureQuest?.properties.challengestatuses ?? []).map((_, i) =>
      positions.has(this.challengeProgressive[this.getChallengeProgressiveRewardPosition(i)]?.position)
    );
  });

  multiGameChallengeProgressiveModalData = inject<Modal>(MAT_DIALOG_DATA);

  get adventureQuestScrollElementSelector() {
    return document.querySelector('.c-adventureQuest__container');
  }

  get adventureQuest() {
    const event = this.eventsRepo.getEventById(this.eventId);

    if (event) {
      this._adventureQuestCopy = event;
    }

    return this._adventureQuestCopy;
  }

  get currentChallengeStatus() {
    return this.adventureQuest.properties.challengestatuses.find(
      (challengeStatuses) => !challengeStatuses.locked && !challengeStatuses.finished
    );
  }

  get currentChallenge() {
    return this.adventureQuest.properties.challengestatuses.find(
      (challenge) => challenge.id === this.currentChallengeStatus?.id
    );
  }

  get challengeProgressive() {
    return this.adventureQuest.properties.challengeprogressive;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.checkArrows();
      this.scrollIntoCurrentChallenge();
    });
  }

  ngOnInit(): void {
    this.eventId = this.multiGameChallengeProgressiveModalData.data.eventId
      ? this.multiGameChallengeProgressiveModalData.data.eventId
      : this.multiGameChallengeProgressiveModalData.data.properties.eventId;

    this.orderRewardsByPrize();
    this.discoverRewards();
  }

  getChallengeProgressiveRewardPosition(i: number): number {
    const progressive = this.challengeProgressive.find((progress) => progress.position === i + 1);

    return this.challengeProgressive.indexOf(progressive);
  }

  discoverRewards(): void {
    let average = 0;

    this.orderedProgressiveChallenge.forEach((progressive, i) => {
      if (i !== 0) {
        average = average + progressive.reward.credits;
      }
    });

    average /= this.adventureQuest.properties.challengeprogressive.length - 1;

    this.adventureQuest.properties.challengeprogressive.forEach((progress, i) => {
      if (
        this.adventureQuest.properties.challengeprogressive[i].reward.credits ===
        this.orderedProgressiveChallenge[0].reward.credits
      ) {
        this.rewardAssetSize.push('reward-three');
      } else if (progress.reward.credits >= average) {
        this.rewardAssetSize.push('reward-two');
      } else {
        this.rewardAssetSize.push('reward-one');
      }
    });
  }

  openInfo(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.AdventureQuestInfoModalComponent, ModalType.View, this.adventureQuest)
    );
  }

  async collectReward(reward: ChallengeProgressiveReward): Promise<void> {
    this.redeemingPositions.update((s) => new Set([...s, reward.position]));
    try {
      const limitedEvent = await firstValueFrom(
        this.limitedTimeEventService.redeemGameProgressiveChallenge(this.adventureQuest.eventId, reward.position)
      );
      if (limitedEvent) {
        this.limitedTimeEventService.updateLimitedTimeEventById(limitedEvent);
        this.scrollIntoCurrentChallenge();
      }
    } catch (err) {
      this.logger.error(err);
    } finally {
      this.redeemingPositions.update((s) => {
        const n = new Set(s);
        n.delete(reward.position);
        return n;
      });
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.AdventureQuestModalComponent, ModalType.View));
  }

  onExpiredChallenge(): void {
    this.closeModal();
    this.limitedTimeEventService.removeLimitedTimeEvent(this.adventureQuest.eventId);
  }

  private checkArrows(): void {
    if (!this.nativeService.isNative()) {
      // Timeout for avoid NG0100 error
      setTimeout(() => {
        this.showArrows.set(
          this.adventureQuestScrollElementSelector &&
            this.adventureQuestScrollElementSelector.clientWidth !==
              this.adventureQuestScrollElementSelector.scrollWidth
        );
      }, 0);
    }
  }

  private scrollIntoCurrentChallenge(): void {
    const pendingChallenge = this.challengeProgressive.find(
      (progress) => this.adventureQuest.current >= progress.position && !progress.redeemed
    );

    if (pendingChallenge) {
      this.scrollByPosition(
        this.challengeProgressive[this.challengeProgressive.indexOf(pendingChallenge)].position - 1
      );
    } else {
      if (this.adventureQuest.current === this.adventureQuest.total) {
        this.scrollByPosition(this.adventureQuest.current - 1);
      } else {
        this.scrollByPosition(this.adventureQuest.current);
      }
    }
  }

  private scrollByPosition(position: number): void {
    document.getElementById(`adventureQuest-scroll-container-${position}`).scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
    });
  }

  private orderRewardsByPrize(): void {
    this.orderedProgressiveChallenge = [...this.adventureQuest.properties.challengeprogressive];

    this.orderedProgressiveChallenge.sort((a, b) => {
      if (a.reward.credits > b.reward.credits) {
        return b.reward.credits - a.reward.credits;
      }
    });
  }
}
